import { SocialLogin } from '@capgo/capacitor-social-login';
import { isNative } from './platform';

export interface GoogleLoginResult {
    idToken: string;
    profile?: {
        email?: string;
        name?: string;
        givenName?: string;
        familyName?: string;
        imageUrl?: string;
    };
}

let initialized = false;

/**
 * Initialize SocialLogin plugin for native platforms
 * Should be called early in app lifecycle
 */
export async function initializeSocialLogin(config: {
    googleWebClientId: string;
    googleIOSClientId?: string;
}): Promise<void> {
    if (!isNative()) {
        console.log('[SocialLogin] Skipping initialization on web platform');
        return;
    }

    if (initialized) {
        console.log('[SocialLogin] Already initialized');
        return;
    }

    try {
        await SocialLogin.initialize({
            google: {
                webClientId: config.googleWebClientId,
                iOSClientId: config.googleIOSClientId,
                iOSServerClientId: config.googleWebClientId,
                mode: 'online',
            },
        });
        initialized = true;
        console.log('[SocialLogin] Initialized successfully');
    } catch (error) {
        console.error('[SocialLogin] Initialization failed:', error);
        throw error;
    }
}

/**
 * Perform Google login on native platform
 * @returns Google ID token and profile info
 */
export async function nativeGoogleLogin(): Promise<GoogleLoginResult> {
    if (!isNative()) {
        throw new Error('Native Google login called on non-native platform');
    }

    if (!initialized) {
        throw new Error('SocialLogin not initialized. Call initializeSocialLogin first.');
    }

    try {
        const result = await SocialLogin.login({
            provider: 'google',
            options: {
                scopes: ['email', 'profile'],
            },
        });

        if (result.provider !== 'google') {
            throw new Error('Unexpected provider response');
        }

        const googleResult = result.result as {
            idToken?: string;
            responseType?: string;
            profile?: {
                email?: string | null;
                name?: string | null;
                givenName?: string | null;
                familyName?: string | null;
                imageUrl?: string | null;
            };
        };

        // Check if we got an online response (has idToken) vs offline response (has serverAuthCode)
        if (googleResult.responseType === 'offline') {
            throw new Error('Received offline response instead of online. Check Google login configuration.');
        }

        if (!googleResult.idToken) {
            throw new Error('No ID token received from Google');
        }

        return {
            idToken: googleResult.idToken,
            profile: googleResult.profile ? {
                email: googleResult.profile.email ?? undefined,
                name: googleResult.profile.name ?? undefined,
                givenName: googleResult.profile.givenName ?? undefined,
                familyName: googleResult.profile.familyName ?? undefined,
                imageUrl: googleResult.profile.imageUrl ?? undefined,
            } : undefined,
        };
    } catch (error) {
        console.error('[SocialLogin] Google login failed:', error);
        throw error;
    }
}

/**
 * Logout from Google on native platform
 */
export async function nativeGoogleLogout(): Promise<void> {
    if (!isNative()) {
        return;
    }

    try {
        await SocialLogin.logout({ provider: 'google' });
        console.log('[SocialLogin] Google logout successful');
    } catch (error) {
        console.error('[SocialLogin] Google logout failed:', error);
    }
}

/**
 * Check if user is logged in with Google on native platform
 */
export async function isGoogleLoggedIn(): Promise<boolean> {
    if (!isNative()) {
        return false;
    }

    try {
        const result = await SocialLogin.isLoggedIn({ provider: 'google' });
        return result.isLoggedIn;
    } catch (error) {
        console.error('[SocialLogin] isLoggedIn check failed:', error);
        return false;
    }
}
